/**
 * Filterlogik für den X83-Teil-Export (Teil-LV an Subunternehmer/Großhandel).
 *
 * Aus einem vollständigen LV wird ein Positionsbereich (Von/Bis-Positionsnummer)
 * und/oder eine Gewerk-Auswahl herausgefiltert. Übergeordnete Titel-/Header-Einträge
 * der gewählten Positionen werden mitgenommen, damit die BoQ-Hierarchie erhalten bleibt.
 */

import {
  comparePositionNumbers,
  isPositionNumberPrefix,
  normalizePositionNumber,
} from './gaeb-price-match';

export interface PartialExportEntry {
  positionNumber?: string | null;
  /** GAEB: 'title' = Header/Gliederungsebene, 'item' = Position */
  type?: string | null;
  /** Alternative Header-Kennzeichnung (Nicht-GAEB-Quellen) */
  isHeader?: boolean;
  gewerk?: string | null;
}

export interface PartialExportFilter {
  /** Nur Positionen dieser Gewerke (leer/undefined = alle Gewerke) */
  gewerke?: string[];
  /** Untere Bereichsgrenze (inklusive), z.B. "1.1" */
  vonPos?: string;
  /** Obere Bereichsgrenze (inklusive, inkl. Unterpositionen), z.B. "1.500" */
  bisPos?: string;
}

function isHeaderEntry(entry: PartialExportEntry): boolean {
  return entry.type === 'title' || entry.isHeader === true;
}

/**
 * Filtert Einträge für den Teil-Export.
 *
 * - Positionen (Nicht-Header) müssen den Gewerk-Filter UND den Bereichsfilter erfüllen.
 * - Bereichsfilter: vonPos <= positionNumber <= bisPos (normalisierter Segmentvergleich).
 *   Unterpositionen der Bis-Grenze zählen mit ("1.500.10" bei bis="1.500").
 * - Header-Einträge werden mitgenommen, wenn ihre Positionsnummer Präfix
 *   mindestens einer gewählten Position ist (Hierarchie-Erhalt).
 * - Die ursprüngliche Reihenfolge bleibt erhalten.
 */
export function filterEntriesForPartialExport<T extends PartialExportEntry>(
  entries: T[],
  filter: PartialExportFilter,
): T[] {
  const gewerkSet =
    filter.gewerke && filter.gewerke.length > 0
      ? new Set(filter.gewerke.map((g) => g.trim()).filter((g) => g.length > 0))
      : null;
  const von = filter.vonPos?.trim() || '';
  const bis = filter.bisPos?.trim() || '';

  const isInRange = (positionNumber: string | null | undefined): boolean => {
    const normalized = normalizePositionNumber(positionNumber);
    if (!normalized && (von || bis)) return false;
    if (von && comparePositionNumbers(normalized, von) < 0) return false;
    if (bis && comparePositionNumbers(normalized, bis) > 0 && !isPositionNumberPrefix(bis, normalized)) {
      return false;
    }
    return true;
  };

  const matchesGewerk = (entry: PartialExportEntry): boolean => {
    if (!gewerkSet) return true;
    const gewerk = entry.gewerk?.trim();
    return Boolean(gewerk && gewerkSet.has(gewerk));
  };

  // 1. Gewählte Positionen (Nicht-Header) bestimmen
  const selectedItems = entries.filter(
    (entry) => !isHeaderEntry(entry) && matchesGewerk(entry) && isInRange(entry.positionNumber),
  );
  if (selectedItems.length === 0) return [];

  const selectedItemSet = new Set<T>(selectedItems);
  const selectedNumbers = selectedItems
    .map((entry) => normalizePositionNumber(entry.positionNumber))
    .filter((n) => n.length > 0);

  // 2. Header mitnehmen, deren Nummer Präfix einer gewählten Position ist
  return entries.filter((entry) => {
    if (selectedItemSet.has(entry)) return true;
    if (!isHeaderEntry(entry)) return false;
    const headerNumber = normalizePositionNumber(entry.positionNumber);
    if (!headerNumber) return false;
    return selectedNumbers.some((itemNumber) => isPositionNumberPrefix(headerNumber, itemNumber));
  });
}
